import { post } from "../../../../service/ApiService";
import { axiosWorkoutInstance } from "../../../../configs/AxiosInstance";
import { findWorkoutGroupType, getWorkoutPeriods } from "../utils/workoutPeriods";
import {
  cacheKey,
  getCachedGet,
  invalidateWorkoutCachePrefix,
} from "./requestCache";

const WORKOUT_READ_CACHE = [
  "GET:/fetch-template-workout",
  "GET:/fetch-target-plan-workout",
  "GET:/fetch-template-workout-summaries",
  "GET:/fetch-target-plan-workout-summaries",
  "GET:/fetch-daily-workout",
  "POST:api/v1/workout/by-ids",
  "GET:api/v1/workout/search",
  "GET:/exercises/list",
];

function invalidateWorkoutReads() {
  WORKOUT_READ_CACHE.forEach((prefix) => invalidateWorkoutCachePrefix(prefix));
}

async function workoutGet(url, fetcher, { ttlMs = 0 } = {}) {
  const key = cacheKey("GET", url, null);
  return getCachedGet(key, fetcher, ttlMs);
}

export { cacheKey, getCachedGet, invalidateWorkoutCachePrefix };

export async function fetchExerciseCatalogList({
  offset = 0,
  limit = 100,
  search = null,
  sortBy = 'name',
  sortOrder = 'asc',
  filters = null,
} = {}) {
  try {
    const params = new URLSearchParams();
    params.set('offset', offset);
    params.set('limit', limit);
    if (search && search.trim()) params.set('search', search.trim());
    params.set('sort_by', sortBy);
    params.set('sort_order', sortOrder);
    if (filters) {
      Object.entries(filters).forEach(([col, f]) => {
        if (f?.min !== null && f?.min !== undefined) params.set(`filter_${col}_min`, f.min);
        if (f?.max !== null && f?.max !== undefined) params.set(`filter_${col}_max`, f.max);
      });
    }
    const url = `/exercises/list?${params.toString()}`;
    const key = cacheKey('GET', url, null);
    const response = await getCachedGet(key, () =>
      axiosWorkoutInstance.get(url).then((r) => r.data)
    );
    return {
      success: true,
      exercises: response?.data?.exercises || [],
      totalCount: response?.data?.total_count ?? 0,
      hasMore: response?.data?.has_more ?? false,
    };
  } catch (error) {
    const { message } = workoutFail(error, 'Failed to fetch exercises.');
    return { success: false, message, exercises: [], totalCount: 0, hasMore: false };
  }
}

function workoutFail(error, fallback = "Request failed.") {
  const data = error?.response?.data;
  if (typeof data === "string" && data.trim()) return { success: false, message: data };
  const detail = data?.detail;
  const message =
    (Array.isArray(detail)
      ? detail.map((d) => d?.msg || d?.message || JSON.stringify(d)).join("; ")
      : typeof detail === "string"
        ? detail
        : null) ||
    data?.message ||
    data?.error ||
    error?.message ||
    fallback;
  return { success: false, message };
}

export function resolveExerciseCatalogId(item) {
  if (!item) return null;
  const id = item.exercise_id ?? item.workout_id ?? item.id;
  return id != null && id !== "" ? id : null;
}

export function normalizeExerciseSearchResults(response) {
  if (!response) return [];
  if (Array.isArray(response)) return response;
  if (Array.isArray(response.results?.results)) return response.results.results;
  if (Array.isArray(response.results)) return response.results;
  if (Array.isArray(response.data?.results)) return response.data.results;
  if (Array.isArray(response.data?.results?.results)) {
    return response.data.results.results;
  }
  return [];
}

export function invalidateWorkoutCommonUtilsCache() {
  sessionStorage.removeItem("workoutCommonUtils");
  invalidateWorkoutCachePrefix("GET:/get-common-utils");
}

export function getWorkoutEventTypeId(sessionUtils) {
  return sessionUtils?.event_types?.[0]?.event_types?.find(
    (x) => x.description === "modify_user_profile"
  )?.id;
}

export async function createWorkoutTemplate(
  post,
  sessionUtils,
  { title, description, security_id },
  workoutCommonUtils
) {
  const eventTypes = sessionUtils?.event_types?.[0]?.event_types || [];
  const event_type_id = getWorkoutEventTypeId(sessionUtils);
  const payload = {
    event_type_id,
    templates: [{ operation: "ADD", title, description, security_id }],
  };
  const response = await saveTemplateName(
    post,
    payload,
    workoutCommonUtils?.workout_group_types || [],
    eventTypes
  );
  const id = response?.data?.data?.templates?.[0]?.id;
  return id ? { success: true, id } : { success: false, message: "Template ID not returned." };
}

export async function updateWorkoutTemplateDetails(
  post,
  sessionUtils,
  { id, title, description, security_id }
) {
  const event_type_id = getWorkoutEventTypeId(sessionUtils);
  if (!event_type_id) return { success: false, message: "Event type not found." };
  try {
    const response = await post(
      "/template/modify-templates",
      {
        event_type_id,
        templates: [{ operation: "UPDATE", id, title, description, security_id }],
      },
      { instance: axiosWorkoutInstance }
    );
    return response?.status === 200
      ? { success: true }
      : { success: false, message: "Failed to update template." };
  } catch (error) {
    return workoutFail(error, "Failed to update template.");
  }
}

export async function updateWorkoutReferenceData(pathSuffix, payload) {
  try {
    const data = await post(`/workout-utils/${pathSuffix}`, payload, {
      instance: axiosWorkoutInstance,
    });
    invalidateWorkoutCommonUtilsCache();
    return { success: true, data };
  } catch (error) {
    return workoutFail(error);
  }
}

export async function modifyExercises(payload) {
  try {
    const data = await post('/modify-exercises', payload, { instance: axiosWorkoutInstance });
    invalidateWorkoutReads();
    return { success: true, data };
  } catch (error) {
    return workoutFail(error, 'Failed to save exercise.');
  }
}

export async function modifyExerciseNames(payload) {
  try {
    const data = await post('/modify-exercise-names', payload, { instance: axiosWorkoutInstance });
    invalidateWorkoutReads();
    return { success: true, data };
  } catch (error) {
    return workoutFail(error, 'Failed to save exercise name.');
  }
}

export async function modifyExerciseContent(payload) {
  try {
    const data = await post('/modify-exercise-content', payload, { instance: axiosWorkoutInstance });
    invalidateWorkoutReads();
    return { success: true, data };
  } catch (error) {
    return workoutFail(error, 'Failed to save exercise content.');
  }
}

export async function fetchExerciseDetail(exerciseId) {
  try {
    const response = await axiosWorkoutInstance.get(`/exercises/${exerciseId}`);
    return { success: true, data: response.data?.data };
  } catch (error) {
    return workoutFail(error, 'Failed to fetch exercise details.');
  }
}

export async function modifyExerciseMuscles(payload) {
  try {
    const data = await post('/modify-exercise-muscles', payload, { instance: axiosWorkoutInstance });
    invalidateWorkoutReads();
    return { success: true, data };
  } catch (error) {
    return workoutFail(error, 'Failed to save exercise muscles.');
  }
}

export async function modifyExerciseEquipment(payload) {
  try {
    const data = await post('/modify-exercise-equipment', payload, { instance: axiosWorkoutInstance });
    invalidateWorkoutReads();
    return { success: true, data };
  } catch (error) {
    return workoutFail(error, 'Failed to save exercise equipment.');
  }
}

export async function modifyExerciseMovementPatterns(payload) {
  try {
    const data = await post('/modify-exercise-movement-patterns', payload, { instance: axiosWorkoutInstance });
    invalidateWorkoutReads();
    return { success: true, data };
  } catch (error) {
    return workoutFail(error, 'Failed to save exercise movement patterns.');
  }
}

export async function modifyExercisePlanesOfMotion(payload) {
  try {
    const data = await post('/modify-exercise-planes-of-motion', payload, { instance: axiosWorkoutInstance });
    invalidateWorkoutReads();
    return { success: true, data };
  } catch (error) {
    return workoutFail(error, 'Failed to save exercise planes of motion.');
  }
}

const COMMON_UTILS_KEY = cacheKey("GET", "/get-common-utils", null);

export const fetchWorkoutCommonUtils = async () => {
  try {
    const data = await getCachedGet(COMMON_UTILS_KEY, () =>
      axiosWorkoutInstance.get("/get-common-utils").then((response) => response.data)
    );
    return data;
  } catch (error) {
    console.error("Failed to fetch workout common utils:", error);
    return null;
  }
};

export const excerciseList = async (getFn, query = "", limit = 20) => {
  try {
    const params = new URLSearchParams();
    params.append("query", query);
    if (limit) params.append("limit", limit);
    const url = `/exercises/search?${params.toString()}`;
    const response = await getFn(url, { instance: axiosWorkoutInstance });
    return normalizeExerciseSearchResults(response);
  } catch (error) {
    console.error("Error fetching exercise items:", error);
    return [];
  }
};

export const fetchDailyWorkoutDetails = async (
  getFn,
  eventTypeId,
  startDate,
  endDate,
  clientId
) => {
  try {
    const params = new URLSearchParams();
    params.append("event_type_id", eventTypeId);
    params.append("start_date", startDate);
    params.append("end_date", endDate);
    params.append("client_id", clientId);
    const url = `/fetch-daily-workout-tracking-details?${params.toString()}`;

    const response = await workoutGet(url, () =>
      getFn(url, { instance: axiosWorkoutInstance })
    );

    return { success: true, data: response?.data || [] };
  } catch (error) {
    console.error("Error fetching daily workout details:", error);
    return {
      success: false,
      message:
        error?.response?.data?.detail ||
        "Failed to fetch daily workout details. Please try again.",
      data: [],
    };
  }
};

// Throws on failure so cache-backed callers can keep their last good rows
// instead of overwriting them with an empty list.
export async function fetchWorkoutTemplateSummaries(getFn, sessionUtils) {
  const eventTypeId = getWorkoutEventTypeId(sessionUtils);
  if (!eventTypeId) return [];
  const url = `/fetch-template-workout-summaries?event_type_id=${eventTypeId}`;
  const response = await workoutGet(url, () =>
    getFn(url, { instance: axiosWorkoutInstance })
  );
  return response?.data?.templates || [];
}

// sessionUtils must be passed as a parameter
export const fitnessTemplate = async (getFn, sessionUtils) => {
  try {
    return await fetchWorkoutTemplateSummaries(getFn, sessionUtils);
  } catch (error) {
    console.error("Error fetching workout templates", error);
    return [];
  }
};

export async function findTemplateById(get, sessionUtils, templateId) {
  const templates = await fitnessTemplate(get, sessionUtils);
  if (!Array.isArray(templates)) return null;
  return templates.find((t) => String(t.id) === String(templateId)) || null;
}


function emptyPeriodResult() {
  return { workouts: [], workoutGroupTypeIds: null };
}

function collectExerciseIdsFromGroups(groups = []) {
  const allIds = new Set();
  groups.forEach((group) => {
    (group.workouts || []).forEach((exercise) => {
      if (exercise.workout_id) allIds.add(exercise.workout_id);
      if (exercise.exercise_id && exercise.exercise_id !== exercise.workout_id) {
        allIds.add(exercise.exercise_id);
      }
    });
  });
  return Array.from(allIds).filter(Boolean);
}

function mapRawWorkouts(rawWorkouts, exerciseDetailsMap) {
  return rawWorkouts.map((exercise) => {
    let exerciseDetails =
      exerciseDetailsMap[exercise.workout_id] ||
      exerciseDetailsMap[exercise.exercise_id] ||
      exercise.exercise_details ||
      null;

    const workoutName =
      exerciseDetails?.exercise ||
      exerciseDetails?.name ||
      exercise.exercise_details?.exercise ||
      exercise.exercise_details?.name ||
      exercise.workout_name ||
      "Unnamed Exercise";

    return {
      id: exercise.id,
      exercise_id:
        exerciseDetails?.exercise_id ||
        exerciseDetails?.id ||
        exercise.exercise_id ||
        exercise.workout_id,
      workout_id: exercise.workout_id,
      workout_type: workoutName,
      workout_name: workoutName,
      short_demo:
        exerciseDetails?.short_demo ||
        exercise.exercise_details?.short_demo ||
        "",
      rest_time_after: exercise.rest_time_after_seconds || 0,
      sequence_order: exercise.sequence_order || 1,
      base_calories_per_rep:
        exerciseDetails?.base_calories_per_rep ||
        exercise.exercise_details?.base_calories_per_rep ||
        0,
      reps_per_minute:
        exerciseDetails?.reps_per_minute ||
        exercise.exercise_details?.reps_per_minute ||
        0,
      difficulty_level:
        exerciseDetails?.difficulty ||
        exercise.exercise_details?.difficulty ||
        "Beginner",
      sets: (exercise.sets || []).map((set) => ({
        id: set.id || set.set_id || 0,
        reps: set.reps || set.target_reps || 0,
        time_seconds: set.time_seconds || set.target_time_seconds || 0,
        rest_time_seconds:
          set.rest_time_seconds || set.target_rest_seconds || 0,
        // Templates persist `weight`; plans use `target_weight`.
        target_weight: set.target_weight ?? set.weight ?? 0,
        target_weight_unit_id:
          set.target_weight_unit_id || set.weight_unit_id || 0,
        weight_unit_id: set.weight_unit_id || set.target_weight_unit_id || 0,
      })),
      exercise_details: exerciseDetails || exercise.exercise_details || null,
    };
  });
}

export function periodWorkoutsFromBundle(
  bundle,
  workout_group_types,
  periodKey
) {
  if (!bundle?.groups) return emptyPeriodResult();

  const groupType = findWorkoutGroupType(workout_group_types, periodKey);
  const workoutGroupID = groupType?.id;
  if (!workoutGroupID) return emptyPeriodResult();

  const matchingGroup = bundle.groups.find(
    (group) => group.workout_group_type_id === workoutGroupID
  );
  if (!matchingGroup) return emptyPeriodResult();

  const exerciseDetailsMap = bundle.exerciseDetailsMap || {};
  return {
    workouts: mapRawWorkouts(matchingGroup.workouts || [], exerciseDetailsMap),
    workoutGroupTypeIds: {
      id: matchingGroup.id,
      workout_group_type_id: workoutGroupID,
    },
  };
}

/** Fetch raw workout groups for a target plan (includes workouts + sets). */
export async function fetchTargetPlanWorkoutDetails(
  getFn,
  eventTypeId,
  planId,
  clientId
) {
  if (!eventTypeId || !planId || !clientId) return [];
  const url = `/fetch-target-plan-workout-details?event_type_id=${eventTypeId}&target_plan_id=${planId}&client_id=${clientId}`;
  const response = await workoutGet(url, () =>
    getFn(url, { instance: axiosWorkoutInstance })
  );
  return response?.data?.workout_groups || [];
}

async function fetchWorkoutGroups(getFn, context, templateId, planId, clientId, eventTypes) {
  const event_type_id = eventTypes?.find(
    (x) => x.description === "modify_user_profile"
  )?.id;
  if (!event_type_id) return [];

  if (context === "template") {
    const url = `/fetch-template-workout-details?event_type_id=${event_type_id}&template_id=${templateId}`;
    const response = await workoutGet(url, () =>
      getFn(url, { instance: axiosWorkoutInstance })
    );
    return response?.data?.workout_groups || [];
  }

  return fetchTargetPlanWorkoutDetails(getFn, event_type_id, planId, clientId);
}

export async function fetchWorkoutGroupsBundle(
  getFn,
  postFn,
  {
    context,
    templateId,
    planId,
    clientId,
    eventTypes,
    workout_group_types,
  }
) {
  let groups = await fetchWorkoutGroups(
    getFn,
    context,
    templateId,
    planId,
    clientId,
    eventTypes
  );

  if (groups.length === 0 && context === "template" && templateId) {
    await updateWorkoutGroupTypes(postFn, eventTypes, templateId, workout_group_types);
    invalidateWorkoutReads();
    groups = await fetchWorkoutGroups(
      getFn,
      context,
      templateId,
      planId,
      clientId,
      eventTypes
    );
  }

  const uniqueIds = collectExerciseIdsFromGroups(groups);
  let exerciseDetailsMap = {};
  if (uniqueIds.length > 0) {
    try {
      const exerciseDetailsResponse = await fetchExerciseDetailsByIds(postFn, uniqueIds);
      if (Array.isArray(exerciseDetailsResponse?.data?.results)) {
        exerciseDetailsResponse.data.results.forEach((exercise) => {
          const exerciseId = exercise.exercise_id || exercise.id;
          if (exerciseId) exerciseDetailsMap[exerciseId] = exercise;
        });
      }
    } catch (error) {
      console.warn("Failed to fetch exercise details:", error);
    }
  }

  return { groups, exerciseDetailsMap };
}

export const fetchSavedWorkouts = async (
  getFn,
  context,
  templateId,
  planId,
  periodKey,
  postFn,
  eventTypes,
  workout_group_types,
  clientId,
  prefetchedBundle = null
) => {
  try {
    const bundle =
      prefetchedBundle ||
      (await fetchWorkoutGroupsBundle(getFn, postFn, {
        context,
        templateId,
        planId,
        clientId,
        eventTypes,
        workout_group_types,
      }));

    return periodWorkoutsFromBundle(bundle, workout_group_types, periodKey);
  } catch (error) {
    console.error("Failed to fetch workouts:", error);
    throw error;
  }
};

export const saveWorkout = async (
  post,
  get,
  context,
  activeWorkout,
  workoutDetails,
  sets,
  periodKey,
  eventTypes,
  workout_group_types,
  workoutGroupTypeIds,
  templateId,
  planId,
  clientId
) => {
  try {
    const eventTypeId = eventTypes?.find(
      (x) => x.description === "modify_user_profile"
    )?.id;

    if (!eventTypeId) {
      throw new Error("Event type ID not found");
    }

    const catalogWorkoutId = resolveExerciseCatalogId(activeWorkout);
    if (!catalogWorkoutId) {
      throw new Error("Exercise catalog ID missing. Search again and re-select the exercise.");
    }

    // One fresh groups fetch covers period resolution + next sequence (avoids a duplicate GET).
    invalidateWorkoutReads();
    let groups = await fetchWorkoutGroups(
      get,
      context,
      templateId,
      planId,
      clientId,
      eventTypes
    );

    if (groups.length === 0 && context === "template" && templateId) {
      await updateWorkoutGroupTypes(post, eventTypes, templateId, workout_group_types);
      invalidateWorkoutReads();
      groups = await fetchWorkoutGroups(
        get,
        context,
        templateId,
        planId,
        clientId,
        eventTypes
      );
    }

    let targetGroupTypeID = workoutGroupTypeIds?.[periodKey]?.id;
    if (!targetGroupTypeID) {
      const groupType = findWorkoutGroupType(workout_group_types, periodKey);
      targetGroupTypeID = groups.find(
        (g) => g.workout_group_type_id === groupType?.id
      )?.id;
    }

    if (!targetGroupTypeID && context === "template" && templateId) {
      await updateWorkoutGroupTypes(post, eventTypes, templateId, workout_group_types);
      invalidateWorkoutReads();
      groups = await fetchWorkoutGroups(
        get,
        context,
        templateId,
        planId,
        clientId,
        eventTypes
      );
      const groupType = findWorkoutGroupType(workout_group_types, periodKey);
      targetGroupTypeID = groups.find(
        (g) => g.workout_group_type_id === groupType?.id
      )?.id;
    }

    if (!targetGroupTypeID) {
      throw new Error(
        `Workout period "${periodKey}" is not set up yet. Refresh the page and try again.`
      );
    }

    // Prefer server max sequence so stale local lists cannot collide (e.g. "Sequence order N exists").
    let sequenceOrder = Number(workoutDetails.sequence_number) || 1;
    const group = groups.find((g) => g.id === targetGroupTypeID);
    const orders = (group?.workouts || []).map((w) => Number(w.sequence_order) || 0);
    if (orders.length) {
      sequenceOrder = Math.max(...orders) + 1;
    }

    const workoutPayload = {
      operation: "ADD",
      ...(context === "template"
        ? {
            template_workout_group_type_id: targetGroupTypeID,
          }
        : {
            target_plan_workout_group_type_id: targetGroupTypeID,
          }),
      workout_id: catalogWorkoutId,
      sequence_order: sequenceOrder,
      rest_time_after_seconds: Number(workoutDetails.workout_rest_time) || 0,
    };

    const workoutResponse = await modifyWorkouts(
      post,
      eventTypeId,
      [workoutPayload],
      context
    );

    if (!workoutResponse || !workoutResponse.data) {
      throw new Error("Failed to create workout");
    }

    let createdWorkoutId = null;
    if (context === "plan") {
      createdWorkoutId = workoutResponse.data.data.target_plan_workouts[0].id;
      // Get the created workout ID
    } else {
      createdWorkoutId = workoutResponse.data.data.workouts[0].id;
    }
    // Step 2: Create sets for the workout
    const activeSets = (sets || []).filter((s) => !s.is_deleted);
    if (activeSets.length > 0) {
      const setsPayload = activeSets.map((set, index) => ({
        operation: "ADD",
        id: "", // Will be generated by API
        ...(context === "template" ? {
          template_workout_id: createdWorkoutId,
          reps: parseInt(set.reps) || 0,
          time_seconds: parseInt(set.time_seconds) || 0,
          rest_time_seconds: parseInt(set.rest_time_seconds) || 0,
          weight: Number(set.target_weight) || 0,
          weight_unit_id: set.weight_unit_id,
        } : {
          target_plan_workout_id: createdWorkoutId,
          target_reps: parseInt(set.reps) || 0,
          target_time_seconds: parseInt(set.time_seconds) || 0,
          target_rest_seconds: parseInt(set.rest_time_seconds) || 0,
          target_weight: Number(set.target_weight) || 0,
          target_weight_unit_id: set.target_weight_unit_id,
        }),
        sequence_order: index + 1,
      }));
    
      await modifyWorkoutSets(post, eventTypeId, setsPayload, context);
    }

    // Surface the id the API already handed us so callers can reconcile their
    // optimistic row directly instead of re-reading the list to find it.
    return { ...workoutResponse, createdWorkoutId };
  } catch (error) {
    console.error("Failed to save workout:", error?.response?.data || error);
    const { message } = workoutFail(error, "Failed to save exercise.");
    throw new Error(message);
  }
};

export const updateWorkout = async (
  post,
  context,
  workoutId,
  sets,
  eventTypes,
  workoutRow = null
) => {
  try {
    // Get event type ID
    const eventTypeId = eventTypes?.find(
      (x) => x.description === "modify_user_profile"
    )?.id;

    if (!eventTypeId) {
      throw new Error("Event type ID not found");
    }

    if (!workoutId) {
      throw new Error("Workout ID is required for update");
    }

    // Persist the workout row itself (rest time, sequence) — sets alone used to
    // be sent, which silently dropped rest_time_after/sequence_order edits.
    if (workoutRow) {
      const isTemplate = context === "template";
      const rowPayload = {
        operation: "UPDATE",
        id: workoutId,
        [isTemplate
          ? "template_workout_group_type_id"
          : "target_plan_workout_group_type_id"]: workoutRow.group_type_id,
        workout_id: workoutRow.workout_id,
        sequence_order: workoutRow.sequence_order || 1,
        rest_time_after_seconds: workoutRow.rest_time_after || 0,
      };
      const rowRes = await modifyWorkouts(post, eventTypeId, [rowPayload], context);
      const rowOk = rowRes?.status === 200 || rowRes?.success;
      if (!rowOk) {
        throw new Error("Failed to update workout details");
      }
    }

    if (sets && sets.length > 0) {
      let sequenceOrder = 0;
      const setsPayload = sets.map((set) => {
        const operation = set.is_deleted ? "DELETE" : set.id ? "UPDATE" : "ADD";
        if (operation !== "DELETE") sequenceOrder += 1;
        return {
          operation,
          id: set.id || null, // Use existing ID for UPDATE/DELETE, empty for ADD
          ...(context === "template" ? {
            template_workout_id: workoutId,
            reps: parseInt(set.reps) || 0,
            time_seconds: parseInt(set.time_seconds) || 0,
            rest_time_seconds: parseInt(set.rest_time_seconds) || 0,
            weight: Number(set.target_weight) || 0,
            weight_unit_id: set.weight_unit_id,
          } : {
            target_plan_workout_id: workoutId,
            target_reps: parseInt(set.target_reps) || 0,
            target_time_seconds: parseInt(set.target_time_seconds) || 0,
            target_rest_seconds: parseInt(set.target_rest_seconds) || 0,
            target_weight: Number(set.target_weight) || 0,
            target_weight_unit_id: set.target_weight_unit_id,
            weight_unit_id: set.weight_unit_id,
          }),
          // Deleted rows must not consume sequence slots for remaining sets.
          sequence_order: operation === "DELETE" ? 0 : sequenceOrder,
        };
      });

      const response = await modifyWorkoutSets(
        post,
        eventTypeId,
        setsPayload,
        context
      );
      return response;
    }
    return { success: true, message: "No sets to update" };
  } catch (error) {
    console.error("Failed to update workout:", error);
    throw error;
  }
};

export const deleteWorkout = async (post, context, workoutId, eventTypes) => {
  try {
    // Get event type ID
    const eventTypeId = eventTypes?.find(
      (x) => x.description === "modify_user_profile"
    )?.id;

    if (!eventTypeId) {
      throw new Error("Event type ID not found");
    }

    if (!workoutId) {
      throw new Error("Workout ID is required for deletion");
    }

    // Only delete the workout - sets will be automatically deleted
    const workoutPayload = {
      operation: "DELETE",
      id: workoutId,
      // Only required fields for DELETE operation
      ...(context === "template"
        ? {
            template_workout_group_type_id: null, // Not needed for DELETE but required by API
          }
        : {
            target_plan_workout_group_type_id: null, // Not needed for DELETE but required by API
          }),
      workout_id: null, // Not needed for DELETE but required by API
      sequence_order: 0,
      rest_time_after_seconds: 0,
    };

    const response = await modifyWorkouts(
      post,
      eventTypeId,
      [workoutPayload],
      context
    );
    return response;
  } catch (error) {
    console.error("Failed to delete workout:", error);
    throw error;
  }
};

export const modifyTemplate = async (
  post,
  sessionUtils,
  templateId,
  operation
) => {
  try {
    // Get event type ID from session utils
    const eventTypeId = sessionUtils?.event_types?.[0]?.event_types?.find(
      (x) => x.description === "modify_user_profile"
    )?.id;

    if (!eventTypeId) {
      throw new Error("Event type ID not found in session utils");
    }

    if (!templateId) {
      throw new Error("Template ID is required for deletion");
    }

    const payload = {
      event_type_id: eventTypeId,
      templates: [
        {
          operation: operation,
          id: templateId,
        },
      ],
    };

    // Make the API call (adjust endpoint as needed)
    const response = await post("/template/modify-templates", payload, {
      instance: axiosWorkoutInstance,
    });
    invalidateWorkoutReads();
    return response;
  } catch (error) {
    console.error("Failed to delete template:", error);
    throw error;
  }
};

export const AssignedPlans = async (
  getFn,
  fromDate,
  toDate,
  userId,
  eventTypeId
) => {
  try {
    const url = `/fetch-target-plan-workout-summaries?start_date=${fromDate}&end_date=${toDate}&client_id=${userId}&event_type_id=${eventTypeId}`;
    const response = await workoutGet(url, () =>
      getFn(url, { instance: axiosWorkoutInstance })
    );
    return response;
  } catch (error) {
    console.error("Failed to fetch Assigned Plans: ", error);
    throw error;
  }
};

export const saveTemplateName = async (
  post,
  payload,
  workout_group_types,
  eventTypes
) => {
  try {
    const response = await post("/template/modify-templates", payload, {
      instance: axiosWorkoutInstance,
    });
    if (payload.templates[0].operation === "CLONE") {
      return response;
    }
    await updateWorkoutGroupTypes(
      post,
      eventTypes,
      response.data.data.templates[0].id,
      workout_group_types
    );
    return response;
  } catch (error) {
    console.error("Failed to Save Template Name: ", error);
    throw error;
  }
};

// Update workout group types (warmup, workout, cooldown) for a template
export const updateWorkoutGroupTypes = async (
  post,
  eventTypes,
  templateId,
  workout_group_types
) => {
  try {
    const event_type_id = eventTypes?.find(
      (x) => x.description === "modify_user_profile"
    )?.id;

    const periods = getWorkoutPeriods(workout_group_types);
    const transformedGroupTypes = periods.map((period, index) => ({
      operation: "ADD",
      template_id: templateId,
      workout_group_type_id: period.groupTypeId,
      sequence_order: index,
    }));

    const payload = {
      event_type_id,
      workout_group_types: transformedGroupTypes,
    };
    const response = await post(
      "/template/modify-template-workout-group-types",
      payload,
      { instance: axiosWorkoutInstance }
    );
    invalidateWorkoutReads();
    return response;
  } catch (error) {
    console.error("Failed to update workout group types:", error);
    throw error;
  }
};

// Modify workouts (template or target plan)
export const modifyWorkouts = async (
  post,
  eventTypeId,
  workouts,
  type = "template"
) => {
  try {
    if (!eventTypeId) {
      throw new Error("Event type ID is required");
    }

    if (!workouts || !Array.isArray(workouts)) {
      throw new Error("Workouts array is required");
    }

    if (!["template", "plan"].includes(type)) {
      throw new Error("Type must be either 'template' or 'plan'");
    }

    const isTemplate = type === "template";
    const groupTypeField = isTemplate
      ? "template_workout_group_type_id"
      : "target_plan_workout_group_type_id";
    const payloadKey = isTemplate ? "workouts" : "target_plan_workouts";
    const endpoint = isTemplate
      ? "/template/modify-template-workouts"
      : "/target-plan/modify-target-plan-workouts";

    // Validate each workout object
    const validatedWorkouts = workouts.map((workout, index) => {
      if (!workout.operation) {
        throw new Error(`Workout at index ${index} is missing operation field`);
      }
      if (!["ADD", "UPDATE", "DELETE"].includes(workout.operation)) {
        throw new Error(
          `Workout at index ${index} has invalid operation. Must be ADD, UPDATE, or DELETE`
        );
      }
      if (workout.operation !== "DELETE") {
        if (!workout[groupTypeField] || workout[groupTypeField] === "") {
          throw new Error(
            `Workout at index ${index} is missing ${groupTypeField}`
          );
        }
        if (!workout.workout_id || workout.workout_id === "") {
          throw new Error(`Workout at index ${index} is missing workout_id`);
        }
      }

      return {
        operation: workout.operation,
        ...(workout.operation !== "ADD" && workout.id ? { id: workout.id } : {}),
        [groupTypeField]: workout[groupTypeField],
        workout_id: workout.workout_id,
        sequence_order: Number(workout.sequence_order) || 0,
        rest_time_after_seconds: Number(workout.rest_time_after_seconds) || 0,
      };
    });

    const payload = {
      event_type_id: eventTypeId,
      [payloadKey]: validatedWorkouts,
    };

    const response = await post(endpoint, payload, {
      instance: axiosWorkoutInstance,
    });
    invalidateWorkoutReads();
    return response;
  } catch (error) {
    console.error(`Failed to modify ${type} workouts:`, error?.response?.data || error);
    const { message } = workoutFail(error, `Failed to modify ${type} workouts`);
    throw new Error(message);
  }
};


// Update workout sequence order (template or target plan)
export const updateWorkoutSequence = async (
  post,
  eventTypeId,
  workouts,
  type = "template"
) => {
  try {
    // This is a convenience wrapper around modifyWorkouts
    // It's specifically for updating sequence orders
    return await modifyWorkouts(post, eventTypeId, workouts, type);
  } catch (error) {
    console.error(`Failed to update workout sequence:`, error);
    throw error;
  }
};

// Modify workout sets (template or target plan)
export const modifyWorkoutSets = async (
  post,
  eventTypeId,
  sets,
  type = "template"
) => {
  try {
    if (!eventTypeId) {
      throw new Error("Event type ID is required");
    }

    if (!sets || !Array.isArray(sets)) {
      throw new Error("Sets array is required");
    }

    if (!["template", "plan"].includes(type)) {
      throw new Error("Type must be either 'template' or 'plan'");
    }

    const isTemplate = type === "template";
    const workoutIdField = isTemplate
      ? "template_workout_id"
      : "target_plan_workout_id";
    const payloadKey = isTemplate ? "sets" : "target_plan_workout_sets";
    const endpoint = isTemplate
      ? "/template/modify-template-workout-sets"
      : "/target-plan/modify-target-plan-workout-sets";

    // Validate each set object
    const validatedSets = sets.map((set, index) => {
      if (!set.operation) {
        throw new Error(`Set at index ${index} is missing operation field`);
      }
      if (!["ADD", "UPDATE", "DELETE"].includes(set.operation)) {
        throw new Error(
          `Set at index ${index} has invalid operation. Must be ADD, UPDATE, or DELETE`
        );
      }
      if (!set[workoutIdField]) {
        throw new Error(`Set at index ${index} is missing ${workoutIdField}`);
      }
      const baseSet = {
        operation: set.operation,
        id: set.id || null,
        [workoutIdField]: set[workoutIdField],
        sequence_order: set.sequence_order || 0,
      };

      if (isTemplate) {
        return {
          ...baseSet,
          reps: set.reps || 0,
          time_seconds: set.time_seconds || 0,
          rest_time_seconds: set.rest_time_seconds || 0,
          weight: set.weight || 0,
          weight_unit_id: set.weight_unit_id ,
        };
      } else {
        return {
          ...baseSet,
          target_reps: set.target_reps || 0,
          target_time_seconds: set.target_time_seconds || 0,
          target_rest_seconds: set.target_rest_seconds || 0,
          target_weight: set.target_weight || 0,
          target_weight_unit_id: set.target_weight_unit_id ,
        };
      }
    });

    const payload = {
      event_type_id: eventTypeId,
      [payloadKey]: validatedSets,
    };

    const response = await post(endpoint, payload, {
      instance: axiosWorkoutInstance,
    });
    invalidateWorkoutReads();
    return response;
  } catch (error) {
    console.error(`Failed to modify ${type} workout sets:`, error);
    throw error;
  }
};

// Fetch exercise details by IDs
export const fetchExerciseDetailsByIds = async (postFn, exerciseIds) => {
  try {
    if (
      !exerciseIds ||
      !Array.isArray(exerciseIds) ||
      exerciseIds.length === 0
    ) {
      throw new Error("Exercise IDs array is required and cannot be empty");
    }

    const sortedIds = [...exerciseIds].filter(Boolean).sort();
    const key = cacheKey("POST", "/exercises/by-ids", sortedIds);
    const payload = { ids: sortedIds };

    return getCachedGet(
      key,
      () =>
        postFn(`/exercises/by-ids`, payload, {
          instance: axiosWorkoutInstance,
        }),
      60_000
    );
  } catch (error) {
    console.error("Error fetching exercise details:", error);
    throw error;
  }
};

// Modify target plan durations
export const modifyTargetPlanDurations = async (post, payload) => {
  try {
    if (!payload.event_type_id) {
      throw new Error("Event type ID is required");
    }

    if (
      !payload.target_plan_durations ||
      !Array.isArray(payload.target_plan_durations)
    ) {
      throw new Error("Target plan durations array is required");
    }

    // Validate each duration object
    payload.target_plan_durations.forEach((duration, index) => {
      if (!duration.operation) {
        throw new Error(
          `Duration at index ${index} is missing operation field`
        );
      }
      if (!["ADD", "UPDATE", "DELETE"].includes(duration.operation)) {
        throw new Error(
          `Duration at index ${index} has invalid operation. Must be ADD, UPDATE, or DELETE`
        );
      }
      if (duration.operation !== "DELETE") {
        if (!duration.assigned_to_id) {
          throw new Error(
            `Duration at index ${index} is missing assigned_to_id`
          );
        }
        if (!duration.active_from) {
          throw new Error(`Duration at index ${index} is missing active_from`);
        }
        if (!duration.active_to) {
          throw new Error(`Duration at index ${index} is missing active_to`);
        }
      }
    });

    const response = await post(
      "/target-plan/modify-target-plan-durations",
      payload,
      { instance: axiosWorkoutInstance }
    );
    return response;
  } catch (error) {
    console.error("Failed to modify target plan durations:", error);
    throw error;
  }
};

// Create a new target plan with template
export const createTargetPlan = async (post, payload) => {
  try {
    if (!payload.event_type_id) {
      throw new Error("Event type ID is required");
    }

    if (!payload.target_plan) {
      throw new Error("Target plan data is required");
    }

    const { target_plan } = payload;

    if (!target_plan.template_id) {
      throw new Error("Template ID is required");
    }

    if (!target_plan.assigned_to_id) {
      throw new Error("Assigned to ID is required");
    }

    if (!target_plan.title) {
      throw new Error("Plan title is required");
    }

    if (!target_plan.description) {
      throw new Error("Plan description is required");
    }

    const response = await post("/target-plan/modify-target-plans", payload, {
      instance: axiosWorkoutInstance,
    });

    if (response?.status === 200) {
      invalidateWorkoutReads();
      return {
        success: true,
        message: "Plan created successfully!",
        data: response.data,
      };
    } else {
      return {
        success: false,
        message: "Failed to create plan. Please try again.",
      };
    }
  } catch (error) {
    console.error("Error creating target plan:", error);
    return workoutFail(error, "An error occurred while creating the plan.");
  }
};

export const modifyTargetPlans = async (post, payload) => {
  try {
    if (!payload.event_type_id) {
      throw new Error('Event type ID is required');
    }

    // API expects singular `target_plan` (same as createTargetPlan).
    const plans = payload.target_plan
      ? [payload.target_plan]
      : payload.target_plans;
    if (!plans || !Array.isArray(plans) || plans.length === 0) {
      throw new Error('Target plan data is required');
    }

    let lastResponse = null;
    for (const target_plan of plans) {
      const response = await post(
        '/target-plan/modify-target-plans',
        { event_type_id: payload.event_type_id, target_plan },
        { instance: axiosWorkoutInstance }
      );
      if (response?.status !== 200) {
        return { success: false, message: 'Failed to update plan.' };
      }
      lastResponse = response;
    }

    invalidateWorkoutReads();
    return {
      success: true,
      message: 'Plan updated successfully!',
      data: lastResponse?.data,
    };
  } catch (error) {
    console.error('Error modifying target plans:', error);
    return {
      success: false,
      message: error.message || 'Failed to update plan.',
    };
  }
};

// Modify target plan weekdays for plan tracking
export const modifyTargetPlanWeekdays = async (post, payload) => {
  try {
    if (!payload.event_type_id) {
      throw new Error("Event type ID is required");
    }

    if (
      !payload.target_plan_weekdays ||
      !Array.isArray(payload.target_plan_weekdays)
    ) {
      throw new Error("Target plan weekdays array is required");
    }

    // Validate each weekday object
    payload.target_plan_weekdays.forEach((weekday, index) => {
      if (!weekday.operation) {
        throw new Error(`Weekday at index ${index} is missing operation field`);
      }
      if (!["ADD", "DELETE"].includes(weekday.operation)) {
        throw new Error(
          `Weekday at index ${index} has invalid operation. Must be ADD or DELETE`
        );
      }

      if (weekday.operation === "ADD") {
        // For ADD operation, id should be null and other fields are required
        if (weekday.id !== null && weekday.id !== undefined) {
          throw new Error(
            `Weekday at index ${index} for ADD operation should have id as null`
          );
        }
        if (!weekday.target_plan_id) {
          throw new Error(
            `Weekday at index ${index} is missing target_plan_id`
          );
        }
        if (weekday.target_plan_duration_id == null || weekday.target_plan_duration_id === '') {
          throw new Error(
            `Weekday at index ${index} is missing target_plan_duration_id`
          );
        }
        if (weekday.weekday_id == null || weekday.weekday_id === '') {
          throw new Error(`Weekday at index ${index} is missing weekday_id`);
        }
      } else if (weekday.operation === "DELETE") {
        // For DELETE operation, id is required (UUID)
        if (!weekday.id) {
          throw new Error(
            `Weekday at index ${index} for DELETE operation is missing id`
          );
        }
      }
    });

    const response = await post(
      "/target-plan/modify-target-plan-weekdays",
      payload,
      { instance: axiosWorkoutInstance }
    );

    if (response?.status === 200) {
      invalidateWorkoutReads();
      return {
        success: true,
        message: "Plan tracking updated successfully!",
        data: response.data,
      };
    } else {
      return {
        success: false,
        message: "Failed to update plan tracking. Please try again.",
      };
    }
  } catch (error) {
    console.error("Error modifying target plan weekdays:", error);
    return {
      success: false,
      message:
        error.message || "An error occurred while updating plan tracking.",
    };
  }
};
